import { buildAgentFacingPublicationModeGuideLines } from "@wakeloop/space-mesh";
import { createGuideErrorFromContract } from "../command-contracts/helpers";
import {
  getCommandEntryContract,
  getCommandHintsFromContract,
} from "../command-contracts/registry";
import { DualViewGuideError } from "./dual-view-guide-error";

type SpaceTargetCommand =
  | "join"
  | "watch"
  | "contract"
  | "status"
  | "guide"
  | "delete";
type SpaceSubcommandTarget = "send" | "history" | "thread" | "password";
type ProfileAction = "set" | "delete" | "show" | "update";
type SpaceAliasCommand = "send" | "tail";
type UpgradeConfirmationScenario = "outdated" | "check_failed";
const SPACE_GATEWAY_PARENT_OPTION_TEXT =
  "parent option: wakeloop space --gateway-url <url>";
const SPACE_CONTRACT_SET_USAGE =
  "wakeloop space [--gateway-url <url>] contract set [--profile <profile-id>] <space-id> [--dispatch-policy <mention_only|listen_all>] [--publication-mode <collaboration|transparent>] [--allowed-actions <post_message,post_status,stay_silent>] [--result-archive-mode <none|space_reply_only|all_final_results>] [--entry-brief <default|none>] [--dispatch-brief <default|none>] [--json] [--password <value>]";
const SPACE_PUBLICATION_SET_USAGE =
  "wakeloop space [--gateway-url <url>] publication set [--profile <profile-id>] <space-id> <collaboration|transparent> --password <space-password> [--json]";
const SPACE_HISTORY_USAGE =
  "wakeloop space [--gateway-url <url>] history [--profile <profile-id>] <space-id> [--limit <n>] [--after-signal <signal-id>] [--around-signal <signal-id> --before <n> --after <n>] [--from-profile <profile-id>] [--kind <text|status|system_notice>] [--json] [--password <value>]";
export type ProfileActionGuideExamples = {
  controllerRefExample?: string;
  transportModeExample?: string;
};

export function createStartAgentIdentityGuideError(summary?: string) {
  return new DualViewGuideError({
    code: "start.agent.identity.required",
    guide: {
      summary:
        summary ??
        "wakeloop start in agent view requires explicit agent profile selection or creation arguments.",
      humanSteps: ["wakeloop view human", "wakeloop start"],
      agentRequiredArgs: [
        "--profile-id <existing-agent-profile-id> or --profile <existing-agent-profile-id|name>",
        "or --kind agent --profile-name <name> (create; profile ID issued by WakeLoop)",
      ],
      agentOptionalArgs: ["--gateway-url <url>"],
      agentCommands: [
        "wakeloop start --view agent --profile-id <existing-agent-profile-id>",
        "wakeloop start --view agent --profile <existing-agent-profile-id-or-name>",
        "wakeloop start --view agent --kind agent --profile-name <new-agent-profile-name>",
        "wakeloop setup --view agent",
        "wakeloop profiles create <new-agent-profile-name> --kind agent --view agent",
        "wakeloop profiles list --view agent",
        "wakeloop space create --profile <agent-profile-id> --name <space-name> --view agent",
        "wakeloop space join --profile <agent-profile-id> <space-id> --view agent",
      ],
      notes: [
        "wakeloop start in agent view selects or creates an Agent Profile and returns Space commands. Run `wakeloop setup --view agent` when this computer needs local service or agent setup.",
        "creating profiles never accepts custom profile IDs; IDs are issued by WakeLoop auth.",
        "if a human is operating this terminal, switch to human view first.",
      ],
    },
    hints: [
      "wakeloop view human",
      "wakeloop start --view agent --profile-id <existing-agent-profile-id>",
      "wakeloop start --view agent --kind agent --profile-name <new-agent-profile-name>",
      "wakeloop setup --view agent",
      "wakeloop space create --profile <agent-profile-id> --name <space-name> --view agent",
    ],
  });
}

export function createSpaceMenuGuideError() {
  return new DualViewGuideError({
    code: "space.menu.interactive.required",
    guide: {
      summary: "wakeloop space menu is interactive in human view only.",
      humanSteps: ["wakeloop view human", "wakeloop space"],
      agentCommands: [
        "wakeloop space [--gateway-url <url>] list [--profile <profile-id>] [--limit <n>] [--json]",
        "wakeloop space [--gateway-url <url>] create [--profile <profile-id>] --name <name> --visibility <public|private> [--guide <text>] [--password <value>] [--join|--no-join] [--output text|ndjson]",
        "wakeloop space [--gateway-url <url>] join [--profile <profile-id>] <space-id> [--password <value>] [--local-echo|--no-local-echo] [--history-limit <n>] [--role <text>] [--mention-alias <token>] [--output text|ndjson]",
        "wakeloop space [--gateway-url <url>] watch [--profile <profile-id>] <space-id> [--password <value>] [--history-limit <n>] [--output text|ndjson]",
        "wakeloop space [--gateway-url <url>] send [--profile <profile-id>] <space-id> [<text...>|--stdin|--file <path>] [--plain] [--password <value>]",
        SPACE_HISTORY_USAGE,
        "wakeloop space [--gateway-url <url>] contract [--profile <profile-id>] <space-id> [--json] [--password <value>]",
        SPACE_CONTRACT_SET_USAGE,
        SPACE_PUBLICATION_SET_USAGE,
        "wakeloop space [--gateway-url <url>] status [--profile <profile-id>] <space-id> [--json] [--password <value>]",
        "wakeloop space [--gateway-url <url>] updates [--profile <profile-id>] <space-id> --after-signal <signal-id> [--limit <n>] [--json] [--password <value>]",
        "wakeloop space [--gateway-url <url>] result [--profile <profile-id>] <space-id> (--dispatch <dispatch-id>|--result-ref <result-ref>) [--json] [--password <value>]",
        "wakeloop space [--gateway-url <url>] guide [--profile <profile-id>] <space-id> [--json] [--password <value>]",
        "wakeloop space [--gateway-url <url>] guide set [--profile <profile-id>] <space-id> <text...> [--json] [--password <value>]",
        "wakeloop space [--gateway-url <url>] guide clear [--profile <profile-id>] <space-id> [--json] [--password <value>]",
        "wakeloop space [--gateway-url <url>] add-agents [--profile <profile-id>] <space-id> (--agent-profile <agent-profile-id...>|--all) [--password <value>]",
        "wakeloop space [--gateway-url <url>] add-human-profiles [--profile <profile-id>] <space-id> (--human-profile <human-profile-id...>|--all) [--password <value>]",
        "wakeloop space [--gateway-url <url>] add-members [--profile <profile-id>] <space-id> (--member <profile-id...>|--all) [--password <value>]",
        "wakeloop space [--gateway-url <url>] remove-members [--profile <profile-id>] <space-id> (--member <profile-id...>|--all) [--password <value>]",
        "wakeloop space [--gateway-url <url>] leave [--profile <profile-id>] <space-id> [--password <value>]",
        "wakeloop space [--gateway-url <url>] delete [--profile <profile-id>] <space-id> [--password <value>]",
        "wakeloop space [--gateway-url <url>] password [--profile <profile-id>] <space-id> (--password <value>|--clear)",
      ],
      notes: [
        "Use explicit subcommands in agent view; do not rely on menu.",
        "Gateway overrides stay on the parent command: `wakeloop space --gateway-url <url> <subcommand> ...`.",
      ],
    },
    hints: [
      "wakeloop space list --profile <profile-id>",
      "wakeloop space create --profile <profile-id> --name spaceship",
      "wakeloop space join --profile <profile-id> <space-id>",
      "wakeloop space watch --profile <profile-id> <space-id>",
    ],
  });
}

export function createSpaceContractSetGuideError(summary?: string) {
  return new DualViewGuideError({
    code: "space.contract.set.arguments.required",
    guide: {
      summary:
        summary ??
        "wakeloop space contract set needs an explicit space ID and at least one patch field in agent view.",
      humanSteps: [
        "wakeloop view human",
        "wakeloop space contract set <space-id>",
        "Follow the guided contract editor.",
      ],
      agentRequiredArgs: ["<space-id>", "at least one patch field"],
      agentOptionalArgs: [
        "--dispatch-policy <mention_only|listen_all>",
        "--publication-mode <collaboration|transparent>",
        "--allowed-actions <post_message,post_status,stay_silent>",
        "--result-archive-mode <none|space_reply_only|all_final_results>",
        "--entry-brief <default|none>",
        "--dispatch-brief <default|none>",
        "--json",
        "--password <value>",
        SPACE_GATEWAY_PARENT_OPTION_TEXT,
        "--profile <profile-id>",
      ],
      agentCommands: [SPACE_CONTRACT_SET_USAGE],
      notes: [
        "agent view must send explicit patch fields; it does not open the interactive editor.",
        "patch writes only the fields you provide.",
      ],
    },
  });
}

export function createSpacePublicationSetGuideError(summary?: string) {
  return new DualViewGuideError({
    code: "space.publication.set.arguments.required",
    guide: {
      summary:
        summary ??
        "wakeloop space publication set needs an explicit space ID, mode, and Space password.",
      humanSteps: [
        "wakeloop view human",
        "wakeloop space publication set <space-id> <collaboration|transparent> --password <space-password>",
      ],
      agentRequiredArgs: [
        "<space-id>",
        "<collaboration|transparent>",
        "--password <space-password>",
      ],
      agentOptionalArgs: [
        "--json",
        SPACE_GATEWAY_PARENT_OPTION_TEXT,
        "--profile <owner-profile-id>",
      ],
      agentCommands: [SPACE_PUBLICATION_SET_USAGE],
      notes: buildAgentFacingPublicationModeGuideLines(),
    },
  });
}

export function createSpaceCreateGuideError(summary?: string) {
  return new DualViewGuideError({
    code: "space.create.arguments.required",
    guide: {
      summary:
        summary ??
        "wakeloop space create requires explicit --name and --visibility public|private in agent/non-interactive mode.",
      humanSteps: [
        "wakeloop view human",
        "wakeloop space",
        'Select "Create a space".',
      ],
      agentRequiredArgs: ["--name <name>", "--visibility <public|private>"],
      agentOptionalArgs: [
        "--guide <text>",
        "--password <value>",
        "--join|--no-join",
        "--output <text|ndjson>",
        SPACE_GATEWAY_PARENT_OPTION_TEXT,
        "--profile <profile-id>",
      ],
      agentCommands: [
        "wakeloop space [--gateway-url <url>] create [--profile <profile-id>] --name <name> --visibility <public|private> [--guide <text>] [--password <value>] [--join|--no-join] [--output text|ndjson]",
        "wakeloop space [--gateway-url <url>] list [--profile <profile-id>] [--limit <n>] [--json]",
        "wakeloop space [--gateway-url <url>] join [--profile <profile-id>] <space-id> [--password <value>] [--local-echo|--no-local-echo] [--history-limit <n>] [--role <text>] [--mention-alias <token>] [--output text|ndjson]",
      ],
      notes: [
        "Agent view does not prompt for missing space names or organization visibility.",
        "Use explicit arguments so create can stay deterministic.",
      ],
    },
    hints: [
      "wakeloop space create --profile <profile-id> --name <space-name> --visibility private --view agent",
      "wakeloop space list --profile <profile-id> --view agent",
    ],
  });
}

export function createSpaceAddMembersGuideError(
  summary?: string,
  entryName:
    | "space.add-agents"
    | "space.add-human-profiles"
    | "space.add-members" = "space.add-members"
) {
  return createGuideErrorFromContract({
    context: {
      includeViewFlag: true,
      profileId: "<profile-id>",
      summary: summary ?? getSpaceAddProfilesDefaultRequiredSummary(entryName),
    },
    guideScenario: getRequiredCommandGuideScenario(entryName),
  });
}

function getSpaceAddProfilesDefaultRequiredSummary(
  entryName:
    | "space.add-agents"
    | "space.add-human-profiles"
    | "space.add-members"
): string {
  switch (entryName) {
    case "space.add-agents":
      return "wakeloop space add-agents in agent/non-interactive mode requires explicit space and Agent Profile arguments.";
    case "space.add-human-profiles":
      return "wakeloop space add-human-profiles in agent/non-interactive mode requires explicit space and Human Profile arguments.";
    case "space.add-members":
      return "wakeloop space add-members in agent/non-interactive mode requires explicit space and member arguments.";
    default:
      assertNever(entryName);
  }
}

function assertNever(value: never): never {
  throw new Error(`unexpected value: ${String(value)}`);
}

export function createSpaceRemoveMembersGuideError(summary?: string) {
  return createGuideErrorFromContract({
    context: {
      includeViewFlag: true,
      profileId: "<profile-id>",
      summary:
        summary ??
        "wakeloop space remove-members in agent/non-interactive mode requires explicit space and member arguments.",
    },
    guideScenario: getRequiredCommandGuideScenario("space.remove-members"),
  });
}

export function createSpaceLeaveGuideError(summary?: string) {
  return new DualViewGuideError({
    code: "space.leave.arguments.required",
    guide: {
      summary:
        summary ??
        "wakeloop space leave in agent/non-interactive mode requires explicit space argument.",
      humanSteps: [
        "wakeloop view human",
        "wakeloop space leave",
        "Choose one space, then confirm leaving it.",
      ],
      agentRequiredArgs: ["<space-id>"],
      agentOptionalArgs: [
        "--profile <profile-id>",
        "--password <value>",
        "--view <agent|human|auto>",
      ],
      agentCommands: [
        "wakeloop space leave <space-id> --profile <profile-id> --view agent",
        "wakeloop space list --profile <profile-id> --view agent",
      ],
      notes: [
        "leave only removes the current profile from the space.",
        "the space creator cannot leave; delete the space instead when only the creator remains.",
      ],
    },
    hints: [
      "wakeloop space leave <space-id> --profile <profile-id> --view agent",
    ],
  });
}

export function createSpaceTargetGuideError(command: SpaceTargetCommand) {
  const optionalArgsByCommand: Record<SpaceTargetCommand, string[]> = {
    join: [
      "--password <value>",
      "--local-echo|--no-local-echo",
      "--history-limit <n>",
      "--role <text>",
      "--mention-alias <token>",
      "--output <text|ndjson>",
      SPACE_GATEWAY_PARENT_OPTION_TEXT,
      "--profile <profile-id>",
    ],
    watch: [
      "--password <value>",
      "--history-limit <n>",
      "--output <text|ndjson>",
      SPACE_GATEWAY_PARENT_OPTION_TEXT,
      "--profile <profile-id>",
    ],
    contract: [
      "--json",
      "--password <value>",
      SPACE_GATEWAY_PARENT_OPTION_TEXT,
      "--profile <profile-id>",
    ],
    status: [
      "--json",
      "--password <value>",
      SPACE_GATEWAY_PARENT_OPTION_TEXT,
      "--profile <profile-id>",
    ],
    guide: [
      "--json",
      "--password <value>",
      SPACE_GATEWAY_PARENT_OPTION_TEXT,
      "--profile <profile-id>",
    ],
    delete: [
      "--password <value>",
      SPACE_GATEWAY_PARENT_OPTION_TEXT,
      "--profile <profile-id>",
    ],
  };

  const commandSignaturesByCommand: Record<SpaceTargetCommand, string[]> = {
    join: [
      "wakeloop space [--gateway-url <url>] join [--profile <profile-id>] <space-id> [--password <value>] [--local-echo|--no-local-echo] [--history-limit <n>] [--role <text>] [--mention-alias <token>] [--output text|ndjson]",
    ],
    watch: [
      "wakeloop space [--gateway-url <url>] watch [--profile <profile-id>] <space-id> [--password <value>] [--history-limit <n>] [--output text|ndjson]",
    ],
    contract: [
      "wakeloop space [--gateway-url <url>] contract [--profile <profile-id>] <space-id> [--json] [--password <value>]",
    ],
    status: [
      "wakeloop space [--gateway-url <url>] status [--profile <profile-id>] <space-id> [--json] [--password <value>]",
    ],
    guide: [
      "wakeloop space [--gateway-url <url>] guide [--profile <profile-id>] <space-id> [--json] [--password <value>]",
      "wakeloop space [--gateway-url <url>] guide set [--profile <profile-id>] <space-id> <text...> [--json] [--password <value>]",
      "wakeloop space [--gateway-url <url>] guide clear [--profile <profile-id>] <space-id> [--json] [--password <value>]",
    ],
    delete: [
      "wakeloop space [--gateway-url <url>] delete [--profile <profile-id>] <space-id> [--password <value>]",
    ],
  };

  return new DualViewGuideError({
    code: `space.${command}.target.required`,
    guide: {
      summary: `wakeloop space ${command} requires explicit space target in agent view.`,
      humanSteps: ["wakeloop view human", "wakeloop space"],
      agentRequiredArgs: [
        `<space-id> (required positional argument for wakeloop space ${command})`,
      ],
      agentOptionalArgs: optionalArgsByCommand[command],
      agentCommands: commandSignaturesByCommand[command],
    },
  });
}

export function createSpaceUpdatesGuideError(summary?: string) {
  return new DualViewGuideError({
    code: "space.updates.arguments.required",
    guide: {
      summary:
        summary ??
        "wakeloop space updates needs an explicit space ID and --after-signal anchor in agent view.",
      humanSteps: ["wakeloop view human", "wakeloop space"],
      agentRequiredArgs: ["<space-id>", "--after-signal <signal-id>"],
      agentOptionalArgs: [
        "--limit <n>",
        "--json",
        "--password <value>",
        SPACE_GATEWAY_PARENT_OPTION_TEXT,
        "--profile <profile-id>",
      ],
      agentCommands: [
        "wakeloop space [--gateway-url <url>] updates [--profile <profile-id>] <space-id> --after-signal <signal-id> [--limit <n>] [--json] [--password <value>]",
      ],
      notes: [
        "updates reads only formal post-anchor public signals.",
        "use --json for machine consumption in agent view.",
      ],
    },
  });
}

export function createSpaceResultGuideError(summary?: string) {
  return new DualViewGuideError({
    code: "space.result.arguments.required",
    guide: {
      summary:
        summary ??
        "wakeloop space result needs an explicit space ID and exactly one selector in agent view.",
      humanSteps: ["wakeloop view human", "wakeloop space"],
      agentRequiredArgs: [
        "<space-id>",
        "one selector: --dispatch <dispatch-id> | --result-ref <result-ref>",
      ],
      agentOptionalArgs: [
        "--json",
        "--password <value>",
        SPACE_GATEWAY_PARENT_OPTION_TEXT,
        "--profile <profile-id>",
      ],
      agentCommands: [
        "wakeloop space [--gateway-url <url>] result [--profile <profile-id>] <space-id> --dispatch <dispatch-id> [--json] [--password <value>]",
        "wakeloop space [--gateway-url <url>] result [--profile <profile-id>] <space-id> --result-ref <result-ref> [--json] [--password <value>]",
      ],
      notes: [
        "exactly one selector is required.",
        "lookups stay scoped to the current space.",
      ],
    },
  });
}

export function createSpaceDeleteHumanApprovalGuideError(summary?: string) {
  return new DualViewGuideError({
    code: "space.delete.human_approval_required",
    guide: {
      summary:
        summary ??
        "space delete requires owner human confirmation in an interactive terminal.",
      humanSteps: [
        "Use a human TTY session with the owner account.",
        "Run: wakeloop view human",
        "Run: wakeloop space delete <space-id>",
        "Confirm deletion when prompted.",
      ],
      agentRequiredArgs: [
        "You cannot delete directly in agent mode/non-interactive sessions.",
      ],
      agentOptionalArgs: [
        SPACE_GATEWAY_PARENT_OPTION_TEXT,
        "--profile <profile-id>",
      ],
      agentCommands: [
        "wakeloop view human",
        "wakeloop space [--gateway-url <url>] delete [--profile <profile-id>] <space-id>",
      ],
      notes: [
        "For agent-created spaces, the owner's human session must approve deletion.",
        "Future enhancement may support web/email approval flow.",
      ],
    },
    hints: ["wakeloop view human", "wakeloop space delete <space-id>"],
  });
}

export function createSpaceSubcommandTargetGuideError(
  command: SpaceSubcommandTarget
) {
  const requiredArgsByCommand: Record<SpaceSubcommandTarget, string[]> = {
    send: [
      "<space-id>",
      "one input source: <text...> | --stdin | --file <path>",
    ],
    history: ["<space-id>"],
    thread: ["<space-id>", "<thread-id>"],
    password: ["<space-id>", "one action: --password <value> | --clear"],
  };
  const optionalArgsByCommand: Record<SpaceSubcommandTarget, string[]> = {
    send: [
      "--password <value>",
      SPACE_GATEWAY_PARENT_OPTION_TEXT,
      "--profile <profile-id>",
    ],
    history: [
      "--limit <n>",
      "--after-signal <signal-id>",
      "--around-signal <signal-id>",
      "--before <n>",
      "--after <n>",
      "--from-profile <profile-id>",
      "--kind <text|status|system_notice>",
      "--json",
      "--password <value>",
      SPACE_GATEWAY_PARENT_OPTION_TEXT,
      "--profile <profile-id>",
    ],
    thread: [
      "--password <value>",
      SPACE_GATEWAY_PARENT_OPTION_TEXT,
      "--profile <profile-id>",
    ],
    password: [SPACE_GATEWAY_PARENT_OPTION_TEXT, "--profile <profile-id>"],
  };
  const commandByCommand: Record<SpaceSubcommandTarget, string> = {
    send: "wakeloop space [--gateway-url <url>] send [--profile <profile-id>] <space-id> [<text...>|--stdin|--file <path>] [--plain] [--password <value>]",
    history: SPACE_HISTORY_USAGE,
    thread:
      "wakeloop space [--gateway-url <url>] thread [--profile <profile-id>] <space-id> <thread-id> [--password <value>]",
    password:
      "wakeloop space [--gateway-url <url>] password [--profile <profile-id>] <space-id> (--password <value>|--clear)",
  };
  const summaryByCommand: Record<SpaceSubcommandTarget, string> = {
    send: "wakeloop space send requires explicit positional arguments in agent view.",
    history:
      "wakeloop space history requires explicit positional arguments in agent view.",
    thread:
      "wakeloop space thread requires a space ID and a thread ID in agent view.",
    password:
      "wakeloop space password needs a space ID and one action (--password or --clear) in agent view.",
  };
  const humanStepsByCommand: Record<SpaceSubcommandTarget, string[]> = {
    send: ["wakeloop view human", "wakeloop space"],
    history: ["wakeloop view human", "wakeloop space"],
    thread: ["wakeloop view human", "wakeloop space"],
    password: [
      "wakeloop view human",
      "wakeloop space",
      'Select "Configure a space" -> "Password" and pick the target space by name',
    ],
  };

  return new DualViewGuideError({
    code: `space.${command}.arguments.required`,
    guide: {
      summary: summaryByCommand[command],
      humanSteps: humanStepsByCommand[command],
      agentRequiredArgs: requiredArgsByCommand[command],
      agentOptionalArgs: optionalArgsByCommand[command],
      agentCommands: [
        "wakeloop space [--gateway-url <url>] list [--profile <profile-id>] [--limit <n>] [--json]",
        commandByCommand[command],
        "wakeloop space [--gateway-url <url>] join [--profile <profile-id>] <space-id> [--history-limit <n>] [--output text|ndjson]",
      ],
    },
  });
}

export function createSpaceProfileRequiredGuideError() {
  return new DualViewGuideError({
    code: "space.profile.required",
    guide: {
      summary:
        "No selected WakeLoop profile is available for this command in the current WakeLoop CLI context.",
      humanSteps: [
        "If this is an interactive human terminal: run `wakeloop whoami`, confirm the current profile, then retry.",
        "If no usable profile exists yet, create one first.",
      ],
      agentRequiredArgs: [
        "--profile <profile-id> when no selected profile exists",
      ],
      agentOptionalArgs: ["--view <auto|human|agent>"],
      agentCommands: [
        "wakeloop profiles list --view agent",
        "wakeloop profiles create <profile-name> --kind agent --view agent",
        "wakeloop profiles set <profile-id> --view agent",
        "wakeloop whoami --profile <profile-id> --view agent",
        "wakeloop space create --profile <profile-id> --name <space-name> --view agent",
        "wakeloop space join --profile <profile-id> <space-id> --view agent",
      ],
      notes: [
        "This error is about the selected WakeLoop profile state, not the sign-in state.",
        "In non-TTY/isolated sessions, always pass --profile explicitly.",
      ],
    },
    hints: [
      "wakeloop profiles create <profile-name> --kind agent --view agent",
      "wakeloop profiles set <profile-id> --view agent",
      "wakeloop space create --profile <profile-id> --name <space-name> --view agent",
    ],
  });
}

export function createSpaceAgentProfileOnlyGuideError(input: {
  profileId: string;
  profileKind: string;
}) {
  return new DualViewGuideError({
    code: "space.profile.agent_only",
    guide: {
      summary: `this Space operation requires an Agent Profile (received ${input.profileKind}:${input.profileId}).`,
      humanSteps: [
        "Use a Human Profile when acting for the user.",
        "Use an Agent Profile only when the operation needs an AI teammate identity.",
      ],
      agentRequiredArgs: ["--profile <agent-profile-id>"],
      agentOptionalArgs: ["--view <agent|human|auto>"],
      agentCommands: [
        "wakeloop profiles list --view agent",
        "wakeloop profiles set <agent-profile-id> --view agent",
        "wakeloop space join <space-id> --profile <agent-profile-id> --view agent",
        "wakeloop space send <space-id> [<text...>|--stdin|--file <path>] --profile <agent-profile-id> --view agent",
      ],
      notes: [
        "First run `wakeloop profiles list --view agent` to find an agent profile ID.",
        "Then rerun the space command with `--profile <agent-profile-id>`.",
        "`--view agent` controls output format. The command-specific operation decides whether a Human Profile or Agent Profile is valid.",
      ],
    },
    hints: [
      "wakeloop profiles list --view agent",
      "wakeloop profiles set <agent-profile-id> --view agent",
      "wakeloop view human",
    ],
  });
}

export function createSpaceUnsupportedProfileKindGuideError(input: {
  profileId: string;
  profileKind: string;
}) {
  return new DualViewGuideError({
    code: "space.profile.unsupported_kind",
    guide: {
      summary: `Space commands require a human or agent profile (received ${input.profileKind}:${input.profileId}).`,
      humanSteps: [
        "Choose a human profile when you are acting for the user.",
        "Choose an agent profile when that agent identity should speak, be added, or be woken in the Space.",
      ],
      agentRequiredArgs: ["--profile <human-or-agent-profile-id>"],
      agentOptionalArgs: ["--view <agent|human|auto>"],
      agentCommands: [
        "wakeloop profiles list --view agent",
        "wakeloop space list --profile <profile-id> --view agent",
        "wakeloop space history <space-id> --profile <profile-id> --kind text --limit 20 --view agent",
      ],
      notes: [
        "`--view agent` controls machine-readable output. It does not require the acting identity to be an Agent Profile.",
        "System profiles are internal and cannot be used as the acting identity for Space commands.",
      ],
    },
    hints: [
      "wakeloop profiles list --view agent",
      "wakeloop space list --profile <profile-id> --view agent",
    ],
  });
}

export function createSpaceAddMembersCurrentProfileGuideError(input: {
  profileId: string;
}) {
  return new DualViewGuideError({
    code: "space.add_members.current_profile_join_required",
    guide: {
      summary:
        "Current profile joins a space with `wakeloop space join`, not `add-members`.",
      humanSteps: [
        "Stay on the current profile and join the Space directly.",
        "Use add-members only for other profiles you want to bring with you.",
      ],
      agentRequiredArgs: ["<space-id>", `--profile ${input.profileId}`],
      agentOptionalArgs: ["--view <agent|human|auto>"],
      agentCommands: [
        `wakeloop space join <space-id> --profile ${input.profileId} --view agent`,
        `wakeloop space send <space-id> "hello" --profile ${input.profileId} --view agent`,
      ],
      notes: [
        "add-members is only for additional profiles, not the profile that is currently acting.",
      ],
    },
    hints: [
      `wakeloop space join <space-id> --profile ${input.profileId} --view agent`,
    ],
  });
}

export function createSpaceRemoveMembersCurrentProfileGuideError(input: {
  profileId: string;
}) {
  return new DualViewGuideError({
    code: "space.remove_members.current_profile_forbidden",
    guide: {
      summary:
        "Current profile cannot remove itself from a space with `remove-members`. Use `wakeloop space leave` instead.",
      humanSteps: [
        "Use `wakeloop space leave` when the current profile should exit the space.",
        "Use `remove-members` only for other members.",
        "If you need a different cleanup action, switch to another owned profile first.",
      ],
      agentRequiredArgs: ["<space-id>", `--profile ${input.profileId}`],
      agentOptionalArgs: ["--view <agent|human|auto>"],
      agentCommands: [
        `wakeloop space leave <space-id> --profile ${input.profileId} --view agent`,
        `wakeloop space history <space-id> --profile ${input.profileId} --kind text --limit 20 --view agent`,
      ],
      notes: [
        "self-removal is blocked for remove-members; leave is the self-exit command.",
      ],
    },
    hints: [
      `wakeloop space leave <space-id> --profile ${input.profileId} --view agent`,
    ],
  });
}

export function createSpaceLeaveCreatorGuideError(input: {
  spaceId?: string;
  message?: string;
}) {
  const spaceArg = input.spaceId?.trim() || "<space-id>";
  return new DualViewGuideError({
    code: "space.leave.creator_forbidden",
    guide: {
      summary:
        input.message ??
        "The space creator cannot leave this space. Delete the space instead when only you remain.",
      humanSteps: [
        "If you are done with the space and only you remain, delete the space.",
        "If other members still need it, keep the creator profile in the space.",
      ],
      agentRequiredArgs: ["<space-id>", "--profile <creator-profile-id>"],
      agentOptionalArgs: ["--view <agent|human|auto>"],
      agentCommands: [
        `wakeloop space delete ${spaceArg} --profile <creator-profile-id> --view human`,
        `wakeloop space status ${spaceArg} --profile <creator-profile-id> --view agent`,
      ],
      notes: [
        "Core is the authority for creator leave checks; CLI only presents the result.",
      ],
    },
    hints: [
      `wakeloop space delete ${spaceArg} --profile <creator-profile-id> --view human`,
    ],
  });
}

export function createSpaceRemoveMembersCreatorGuideError(input?: {
  profileId?: string;
}) {
  const creatorProfileId = input?.profileId?.trim();
  return new DualViewGuideError({
    code: "space.remove_members.creator_forbidden",
    guide: {
      summary: "The space creator cannot be removed from this space.",
      humanSteps: [
        "Remove other members instead of the space creator.",
        "If the goal is to retire the space, delete the space from a human owner session.",
      ],
      agentRequiredArgs: ["<space-id>"],
      agentOptionalArgs: [
        "--member <other-profile-id...>",
        "--all",
        "--profile <profile-id>",
      ],
      agentCommands: [
        "wakeloop space remove-members <space-id> --profile <profile-id> --member <other-profile-id> --view agent",
        "wakeloop view human",
        "wakeloop space delete <space-id>",
      ],
      notes: [
        ...(creatorProfileId
          ? [`blocked creator profile: ${creatorProfileId}`]
          : []),
      ],
    },
    hints: [
      "wakeloop space remove-members <space-id> --profile <profile-id> --member <other-profile-id> --view agent",
      "wakeloop view human",
      "wakeloop space delete <space-id>",
    ],
  });
}

export function createSpaceSendInputRequiredGuideError() {
  return new DualViewGuideError({
    code: "send.input.required",
    guide: {
      summary:
        "wakeloop space send requires one input source: <text...> or --stdin or --file <path>.",
      humanSteps: [
        'Use quick text: wakeloop space send <space-id> "hello"',
        "Or pipe stdin: echo 'hello' | wakeloop space send <space-id> --stdin",
        "Or send file: wakeloop space send <space-id> --file /tmp/message.txt",
      ],
      agentRequiredArgs: ["exactly one input source"],
      agentOptionalArgs: [
        SPACE_GATEWAY_PARENT_OPTION_TEXT,
        "--profile <profile-id>",
      ],
      agentCommands: [
        'wakeloop space send --profile <profile-id> <space-id> "hello"',
        "cat /tmp/message.txt | wakeloop space send --profile <profile-id> <space-id> --stdin",
        "wakeloop space send --profile <profile-id> <space-id> --file /tmp/message.txt",
        'wakeloop space send --profile <profile-id> <space-id> --plain "literal @Agent(wake) text"',
      ],
      notes: [
        "Prefer --stdin/--file for multiline or shell-sensitive content.",
        "Use --plain only when @Name(wake), @Name(reference), or @Name(cc) should be sent as literal text.",
      ],
    },
  });
}

export function createSpaceSendInputConflictGuideError() {
  return new DualViewGuideError({
    code: "send.input.conflict",
    guide: {
      summary:
        "wakeloop space send received conflicting input sources. Use exactly one of: <text...>, --stdin, --file <path>.",
      humanSteps: [
        "Pick only one source per command.",
        "Remove extra input flags and retry.",
      ],
      agentRequiredArgs: ["exactly one input source"],
      agentCommands: [
        'wakeloop space send --profile <profile-id> <space-id> "hello"',
        "wakeloop space send --profile <profile-id> <space-id> --stdin",
        "wakeloop space send --profile <profile-id> <space-id> --file /tmp/message.txt",
        'wakeloop space send --profile <profile-id> <space-id> --plain "literal @Agent(wake) text"',
      ],
      notes: [
        "Do not combine text with --stdin/--file.",
        "Use --plain only when @Name(wake), @Name(reference), or @Name(cc) should be sent as literal text.",
      ],
    },
  });
}

export function createSpaceSendInputFileReadFailedGuideError(
  path: string,
  reason: string
) {
  return new DualViewGuideError({
    code: "send.input.file_read_failed",
    guide: {
      summary: `failed to read send input file: ${path}`,
      humanSteps: [
        "Check file path and permissions.",
        "Retry with a readable UTF-8 text file.",
      ],
      agentRequiredArgs: ["--file <path> must exist and be readable"],
      agentCommands: [
        "ls -l <path>",
        "cat <path>",
        "wakeloop space send --profile <profile-id> <space-id> --file <path>",
      ],
      notes: [`read error: ${reason}`],
    },
  });
}

export function createSpaceSendInputEmptyGuideError() {
  return new DualViewGuideError({
    code: "send.input.empty",
    guide: {
      summary: "send input is empty after normalization.",
      humanSteps: [
        "Provide non-empty text.",
        "For stdin/file mode, ensure content is not blank.",
      ],
      agentRequiredArgs: ["non-empty message text"],
      agentCommands: [
        'wakeloop space send --profile <profile-id> <space-id> "hello"',
        "echo 'hello' | wakeloop space send --profile <profile-id> <space-id> --stdin",
      ],
    },
  });
}

export function createProfileMenuGuideError() {
  return new DualViewGuideError({
    code: "profile.menu.interactive.required",
    guide: {
      summary: "wakeloop profiles menu is interactive in human view only.",
      humanSteps: ["wakeloop view human", "wakeloop profile"],
      agentRequiredArgs: [
        "set: <profile-id> (or --profile <id>)",
        "delete: <profile-id> (or --profile <id>)",
        "delete in non-interactive mode: --force",
      ],
      agentOptionalArgs: ["--view <auto|human|agent>"],
      agentCommands: [
        "wakeloop profiles create <profileName> --kind agent [--name <profileName>]",
        "wakeloop start --view human",
        "wakeloop profiles list | wakeloop profiles ls",
        "wakeloop profiles show <profile-id> [--profile <id>]",
        "wakeloop profiles set <profile-id> [--profile <id>]",
        "wakeloop profiles update <profile-id> [--profile <id>] [--name <profileName>] [--workspace-mode <wakeloop-managed|custom>] [--workspace-path <path>]",
        "wakeloop profiles delete <profile-id> [--profile <id>] --force",
      ],
    },
    hints: [
      "wakeloop view human",
      "wakeloop profile",
      "wakeloop profiles list",
      "wakeloop profiles set <profile>",
    ],
  });
}

export function createProfileCreateGuideError(summary: string) {
  return new DualViewGuideError({
    code: "profile.create.arguments.required",
    guide: {
      summary: `wakeloop profiles create: ${summary}`,
      humanSteps: [
        "wakeloop view human",
        "wakeloop profile",
        "Select: Create profile -> Profile kind (Human or Agent) -> Profile name",
      ],
      agentRequiredArgs: [
        "--kind agent",
        "<profileName> (positional) or --name <profileName>",
      ],
      agentOptionalArgs: ["--view <auto|human|agent>"],
      agentCommands: [
        "wakeloop profiles create <profileName> --kind agent --view agent",
        "wakeloop profiles list --view agent",
        "wakeloop profiles set <profile-id> --view agent",
        "wakeloop whoami --profile <profile-id> --view agent",
        "wakeloop space create --profile <profile-id> --name <space-name> --view agent",
        "wakeloop space join --profile <profile-id> <space-id> --view agent",
      ],
      notes: [
        "agent view can only create agent profiles for the current authenticated owner.",
        "to create a human profile, switch to human view first.",
        "created profiles always include ownerUserId from the authenticated session.",
      ],
    },
  });
}

export function createProfileActionGuideError(
  action: ProfileAction,
  summary: string,
  _examples?: ProfileActionGuideExamples
) {
  const requiredArgsByAction: Record<ProfileAction, string[]> = {
    show: ["<profile-id> (positional) or --profile <id>"],
    set: ["<profile-id> (positional) or --profile <id>"],
    delete: [
      "<profile-id> (positional) or --profile <id>",
      "--force (required in non-interactive mode)",
    ],
    update: [
      "<profile-id> (positional) or --profile <id>",
      "at least one update field: --name or --workspace-mode",
    ],
  };

  const commandByAction: Record<ProfileAction, string> = {
    show: "wakeloop profiles show <profile-id> [--profile <id>] [--view <auto|human|agent>]",
    set: "wakeloop profiles set <profile-id> [--profile <id>] [--view <auto|human|agent>]",
    delete:
      "wakeloop profiles delete <profile-id> [--profile <id>] --force [--view <auto|human|agent>]",
    update:
      "wakeloop profiles update <profile-id> [--profile <id>] [--name <profileName>] [--workspace-mode <wakeloop-managed|custom>] [--workspace-path <path>] [--view <auto|human|agent>]",
  };

  return new DualViewGuideError({
    code: `profile.${action}.arguments.required`,
    guide: {
      summary: `wakeloop profiles ${action}: ${summary}`,
      humanSteps: ["wakeloop view human", "wakeloop profile"],
      agentRequiredArgs: requiredArgsByAction[action],
      agentOptionalArgs: ["--view <auto|human|agent>"],
      agentCommands: [
        commandByAction[action],
        "wakeloop profiles list",
        "wakeloop start --view human",
        "wakeloop start --kind agent --profile-id <existing-agent-profile-id>",
        "wakeloop start --kind agent --profile-name <new-agent-profile-name>",
      ],
    },
  });
}

export function createSkillsMenuGuideError(input: {
  storeDir: string;
  knownAgentIds: string[];
  agentExampleArg?: string;
  skills: Array<{
    id: string;
    name: string;
    relativePath: string;
    absolutePath: string;
  }>;
}) {
  const agentExampleArg = normalizeGuideExample(
    input.agentExampleArg,
    "<id...>"
  );
  const notes = [`WakeLoop skills store: ${input.storeDir}`];
  if (input.knownAgentIds.length > 0) {
    notes.push(`known agent ids: ${input.knownAgentIds.join(", ")}`);
  } else {
    notes.push("known agent ids: none detected");
  }

  if (input.skills.length > 0) {
    notes.push("local WakeLoop skills:");
    for (const skill of input.skills) {
      notes.push(`  - ${skill.name} (${skill.relativePath})`);
    }
  } else {
    notes.push("local WakeLoop skills: none");
  }

  return new DualViewGuideError({
    code: "skills.menu.interactive.required",
    guide: {
      summary: "wakeloop skills menu is interactive in human view only.",
      humanSteps: ["wakeloop view human", "wakeloop skills"],
      agentCommands:
        getCommandHintsFromContract({
          context: {
            agentExampleArg,
            knownAgentIds: input.knownAgentIds,
            skills: input.skills.map((skill) => ({
              name: skill.name,
              relativePath: skill.relativePath,
            })),
            storeDir: input.storeDir,
          },
          entryName: "skills.menu",
        }) ?? [],
      notes,
    },
    hints: [
      "wakeloop view human",
      "wakeloop skills",
      "wakeloop skills list",
      "wakeloop skills check",
      "wakeloop skills update",
      "wakeloop skills install --all",
    ],
  });
}

export function createSkillsActionGuideError(input: {
  action: "ensure" | "install" | "uninstall";
  agentExampleArg?: string;
}) {
  return createGuideErrorFromContract({
    context: {
      agentExampleArg: normalizeGuideExample(input.agentExampleArg, "<id...>"),
    },
    guideScenario: getRequiredCommandGuideScenario(`skills.${input.action}`),
  });
}

function normalizeGuideExample(
  value: string | undefined,
  fallback: string
): string {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : fallback;
}

export function createSpaceAliasTargetGuideError(command: SpaceAliasCommand) {
  const requiredArgsByCommand: Record<SpaceAliasCommand, string[]> = {
    send: [
      "--space <space-id>",
      "one input source: <text...> | --stdin | --file <path>",
    ],
    tail: ["--space <space-id>"],
  };
  const optionalArgsByCommand: Record<SpaceAliasCommand, string[]> = {
    send: [
      "--profile <id>",
      "--base-url <url>",
      "--password <value>",
      "--stdin",
      "--file <path>",
    ],
    tail: [
      "--profile <id>",
      "--base-url <url>",
      "--password <value>",
      "--limit <n>",
      "--json",
    ],
  };
  const commandByAlias: Record<SpaceAliasCommand, string> = {
    send: "wakeloop send [<text...>|--stdin|--file <path>] --space <space-id> [--plain] [--password <value>] [--profile <id>] [--base-url <url>]",
    tail: "wakeloop tail --space <space-id> [--limit <n>] [--after-signal <signal-id>] [--around-signal <signal-id> --before <n> --after <n>] [--from-profile <profile-id>] [--kind <text|status|system_notice>] [--json] [--password <value>] [--profile <id>] [--base-url <url>]",
  };
  const preferredCommand: Record<SpaceAliasCommand, string> = {
    send: "wakeloop space [--gateway-url <url>] send [--profile <profile-id>] <space-id> [<text...>|--stdin|--file <path>] [--plain] [--password <value>]",
    tail: SPACE_HISTORY_USAGE,
  };

  return new DualViewGuideError({
    code: `space.${command}.alias.target.required`,
    guide: {
      summary: `wakeloop ${command} requires explicit --space argument in agent view.`,
      humanSteps: ["wakeloop view human", "wakeloop space"],
      agentRequiredArgs: requiredArgsByCommand[command],
      agentOptionalArgs: optionalArgsByCommand[command],
      agentCommands: [commandByAlias[command], preferredCommand[command]],
      notes: ["Alias command detected. Prefer `wakeloop space ...` commands."],
    },
  });
}

export function createResetConfirmationGuideError() {
  return new DualViewGuideError({
    code: "reset.confirmation.required",
    guide: {
      summary:
        "wakeloop reset in agent view requires explicit confirmation flag.",
      humanSteps: ["wakeloop view human", "wakeloop reset"],
      agentRequiredArgs: ["--yes"],
      agentCommands: ["wakeloop reset --yes"],
      notes: [
        "`wakeloop reset` is destructive. Keep `--yes` explicit in agent view.",
      ],
    },
    hints: ["wakeloop reset --yes"],
  });
}

export function createUpgradeConfirmationGuideError(
  scenario: UpgradeConfirmationScenario
) {
  const summary =
    scenario === "outdated"
      ? "wakeloop upgrade in agent view requires --yes before installing updates."
      : "wakeloop upgrade in agent view requires --yes when version check fails.";

  const notes =
    scenario === "outdated"
      ? [
          "Use `wakeloop upgrade --check` to inspect versions without installing.",
        ]
      : [
          "Use `wakeloop upgrade --check` to inspect status first.",
          "When check fails, install requires explicit `--yes` in agent view.",
        ];

  return new DualViewGuideError({
    code: `upgrade.confirmation.required.${scenario}`,
    guide: {
      summary,
      humanSteps: ["wakeloop view human", "wakeloop upgrade"],
      agentRequiredArgs: ["--yes"],
      agentOptionalArgs: ["--check"],
      agentCommands: ["wakeloop upgrade --yes", "wakeloop upgrade --check"],
      notes,
    },
    hints: ["wakeloop upgrade --yes", "wakeloop upgrade --check"],
  });
}

export function createStartAuthenticationGuideError() {
  return new DualViewGuideError({
    code: "start.auth.required",
    guide: {
      summary:
        "wakeloop start requires sign-in before WakeLoop profile selection.",
      humanSteps: ["wakeloop view human", "wakeloop login", "wakeloop start"],
      agentCommands: [
        "wakeloop auth status --view agent",
        "wakeloop login --view agent --method ott --ott <token>",
        "wakeloop start --view agent",
      ],
      notes: [
        "If a human is operating this terminal, switch to human view first.",
      ],
    },
    hints: ["wakeloop login", "wakeloop auth status", "wakeloop start"],
  });
}

export function createAuthLoginGuideError(summary: string) {
  return createGuideErrorFromContract({
    context: {
      includeViewFlag: true,
      summary,
    },
    guideScenario: getRequiredCommandGuideScenario("auth.login"),
  });
}

function getRequiredCommandGuideScenario(
  entryName:
    | "auth.login"
    | "skills.ensure"
    | "skills.install"
    | "skills.uninstall"
    | "space.add-agents"
    | "space.add-human-profiles"
    | "space.add-members"
    | "space.remove-members"
) {
  const contract = getCommandEntryContract(entryName);
  const guideScenario = contract?.guideScenarios?.required;
  if (!guideScenario) {
    throw new Error(`missing required guide scenario for ${entryName}`);
  }
  return guideScenario;
}

export function createDoctorFailedGuideError() {
  return new DualViewGuideError({
    code: "doctor.summary.failed",
    guide: {
      summary: "wakeloop doctor detected one or more failed checks.",
      humanSteps: ["wakeloop view human", "wakeloop doctor"],
      agentOptionalArgs: ["--profile <id>", "--gateway-url <url>"],
      agentCommands: [
        "wakeloop doctor --view agent [--profile <id>] [--gateway-url <url>]",
        "wakeloop whoami --view agent",
        "wakeloop auth status --view agent",
      ],
      notes: [
        "Inspect preceding `doctor.check.fail` outputs for exact failure details.",
      ],
    },
  });
}
